import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldArray, FieldArrayPath, FieldPathByValue, FieldValues, Path, PathValue, useFormContext,
} from 'react-hook-form'
import { TransportDocument } from 'features/declaration/models/schemas/documentSchemas'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { excludeDeleted } from 'shared/lib/common-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'
import DocumentFiles from 'features/declaration/document/components/DocumentFiles'
import DocumentDropdownArea from 'features/declaration/document/components/DocumentDropdownArea'
import dotLeafPaths from 'helpers/dot-leaf-paths'
import { ArrayPathByValue } from 'shared/rhf/models/types'
import { NctsScope } from 'shared/ncts/types'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import generateTransportDocumentReference from 'shared/ncts/common/transport-document-reference-util'

const ICS_TRANSPORT_DOCUMENTS = [
  'TRANSPORT_DOCUMENT_TYPE_N703',
  'TRANSPORT_DOCUMENT_TYPE_N704',
  'TRANSPORT_DOCUMENT_TYPE_N705',
  'TRANSPORT_DOCUMENT_TYPE_N714',
  'TRANSPORT_DOCUMENT_TYPE_N720',
  'TRANSPORT_DOCUMENT_TYPE_N722',
  'TRANSPORT_DOCUMENT_TYPE_N730',
  'TRANSPORT_DOCUMENT_TYPE_N740',
  'TRANSPORT_DOCUMENT_TYPE_N741',
  'TRANSPORT_DOCUMENT_TYPE_N750',
  'TRANSPORT_DOCUMENT_TYPE_N760',
] as const

type FieldValuesWithTransportDocuments = FieldValues & {
  transportDocument: TransportDocument[]
}

type TransportDocumentRelPath = dotLeafPaths<TransportDocument>

const scope = 'transportDocument'
type TransportDocumentBasePath<TForm extends FieldValuesWithTransportDocuments> = Extract<Path<TForm>, `${typeof scope}.${number}`>
type TransportDocumentFilesArrayPath<TForm extends FieldValuesWithTransportDocuments> = Extract<Path<TForm>,
  `${typeof scope}.${number}.files`
>
type FileArrayPath<TForm extends FieldValues> = Extract<FieldArrayPath<TForm>, FieldPathByValue<TForm, FileType[]>>

type TransportDocumentsArrayPath<TForm extends FieldValues> = ArrayPathByValue<TForm, TransportDocument>

type TransportDocumentsProps<TForm extends FieldValuesWithTransportDocuments> = {
  createDefault: () => FieldArray<TForm, TransportDocumentsArrayPath<TForm>>
  isEns: boolean
  country: string
  scope: NctsScope
}

function TransportDocuments<
  TForm extends FieldValuesWithTransportDocuments,
>({
  scope: formScope,
  createDefault,
  isEns,
  country,
}: Readonly<TransportDocumentsProps<TForm>>) {
  const { t } = useTranslation()

  const { control, getValues, setValue } = useFormContext<TForm>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TForm, TransportDocument, TransportDocumentsArrayPath<TForm>>({
    control,
    name: 'transportDocument' as TransportDocumentsArrayPath<TForm>,
  })

  const [, transportDocumentTypesOptions] = useCodelist('TRANSPORT_DOCUMENTS')

  const isAddButtonLocked = useMemo(() => {
    if (!isEns) {
      return false
    }
    return visibleFields.map((field) => field.field).filter(excludeDeleted).length >= 1
  }, [isEns, visibleFields])

  const transportDocumentOptions = useMemo(() => {
    if (isEns) {
      return transportDocumentTypesOptions.filter((option) => ICS_TRANSPORT_DOCUMENTS.includes(option.value))
    }
    return transportDocumentTypesOptions
  }, [transportDocumentTypesOptions, isEns])

  const removeDocument = useCallback((index: number) => {
    const innerItemScope = `${scope}.${index}` as TransportDocumentBasePath<TForm>
    const filesPath = `${innerItemScope}.files` as TransportDocumentFilesArrayPath<TForm>
    const filesOfRemovedDocument = getValues(filesPath)
    const updatedFiles = filesOfRemovedDocument.map((f) => (
      {
        ...f,
        deleted: true,
      }
    ))
    setValue(filesPath, updatedFiles as PathValue<TForm, typeof filesPath>)

    softRemove(index)
  }, [softRemove, getValues, setValue])

  const addDocument = useCallback(() => {
    const newDocument = createDefault()

    if (!isEns) {
      appendWithSequence(newDocument)
      return
    }

    // ICS2 ENS: prefill a unique master transport document (CMR) reference (carrier EORI + ULID).
    // It is only a default - the user may overwrite it with a real CMR note number.
    const carrierEori = getValues('carrier.identificationNumber' as Path<TForm>) as string | undefined
    appendWithSequence({
      ...(newDocument as TransportDocument),
      referenceNumber: generateTransportDocumentReference(carrierEori),
    } as FieldArray<TForm, TransportDocumentsArrayPath<TForm>>)
  }, [appendWithSequence, createDefault, getValues, isEns])

  // ENS only: mint a fresh unique reference for an existing row, so a uniqueness clash
  // is a one-click fix - no need to hand-edit or remove/re-add the row.
  const regenerateReference = useCallback((itemScope: TransportDocumentBasePath<TForm>) => {
    const carrierEori = getValues('carrier.identificationNumber' as Path<TForm>) as string | undefined
    setValue(
      `${itemScope}.referenceNumber` as Path<TForm>,
      generateTransportDocumentReference(carrierEori) as PathValue<TForm, Path<TForm>>,
      { shouldDirty: true, shouldValidate: true },
    )
  }, [getValues, setValue])

  const getValueAtIndex = (index: number, relPath: TransportDocumentRelPath) => {
    const fullPath = `transportDocument.${index}.${relPath}` as Path<TForm>

    return getValues(fullPath)
  }

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.documentType`),
      ...visibleFields.map((
        f,
      ) => `${scope}.${f.index}.referenceNumber`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<TForm>(formScope, paths)
  const isAddForbidden = fieldRequirements.transportDocument?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-documents"
        headingLabel={t('declaration.p5.transportDocuments')}
        fieldRule={fieldRequirements.transportDocument}
      >
        {visibleFields.map((fieldWithIndex) => {
          const transportDocument = fieldWithIndex.field
          const innerItemScope = `transportDocument.${fieldWithIndex.index}` as TransportDocumentBasePath<TForm>
          const filesPath = `${innerItemScope}.files` as FileArrayPath<TForm>
          const documentId = getValueAtIndex(fieldWithIndex.index, 'id')
          const referenceNumberRule = fieldRequirements[`${innerItemScope}.referenceNumber`]
          const referenceNumberInput = (
            <Input
              type="text"
              field={`${innerItemScope}.referenceNumber`}
              labelKey="declaration.p5.transportDocumentReferenceNumber"
              fieldRule={referenceNumberRule}
            />
          )

          return (
            <FormFieldArray
              key={`transportDocument_${transportDocument.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => removeDocument(fieldWithIndex.index)}
              sequenceNumber={transportDocument.sequenceNumber + 1}
              hidden={transportDocument.deleted}
            >
              <div className="field-array-row">
                <FormField
                  labelKey="declaration.p5.transportDocumentType"
                  fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.documentType`}
                    labelKey="declaration.p5.transportDocumentType"
                    options={transportDocumentOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.documentType`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.transportDocumentReferenceNumber"
                  fieldRule={referenceNumberRule}
                  tooltipContent={isEns ? t('tooltips.transportDocumentReference') : undefined}
                  tooltipPlacement="top"
                >
                  {isEns ? (
                    <div className="input-group">
                      {referenceNumberInput}
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-light h-100 border"
                          onClick={() => regenerateReference(innerItemScope)}
                          disabled={referenceNumberRule?.requirement === 'forbidden'}
                          title={t('buttons.regenerate')}
                          aria-label={t('buttons.regenerate')}
                        >
                          <i className="fal fa-sync" />
                        </button>
                      </div>
                    </div>
                  ) : referenceNumberInput}
                </FormField>
              </div>
              <DocumentFiles<TForm>
                key={`documentFileNames${transportDocument.id + transportDocument.sequenceNumber}`}
                name={filesPath}
              />
              <DocumentDropdownArea
                key={`documents${transportDocument.sequenceNumber + transportDocument.id}`}
                name={filesPath}
                country={country}
                documentId={documentId}
              />
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden || isAddButtonLocked ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={addDocument}
              disabled={isAddForbidden || isAddButtonLocked}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewDocument')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default TransportDocuments
